/**
 * chips.ts — chip palette for Calculator Studio HTML editors.
 *
 * Mirrors the token list S4's `buildChipPalette(spec)` derives on the backend
 * (config-eval/present). The palette is generated from the LIVE config so it
 * updates as the admin adds inputs / outputs / recommendation slots.
 *
 * Token forms (canonical, per spec §7):
 *   {{input.<key>}}   {{output.<key>}}   {{rec.primary.sku}} etc.
 *   {{lineItems}}  {{roiGrid}}  {{engineeringNotes}}  + customer/company/totals scalars
 */

import type { CalcSpec, StudioInputField } from '../../../api/calculatorBuilder';

export interface Chip {
  token: string; // without braces, e.g. "input.people"
  label: string;
  group: 'Inputs' | 'Outputs' | 'Recommendation' | 'Blocks' | 'Customer' | 'Company' | 'Totals';
  /** fragment tokens render as a wide block chip (tables/grids). */
  fragment?: boolean;
}

const SCALAR_CUSTOMER: Chip[] = [
  { token: 'customer.name', label: 'Customer Name', group: 'Customer' },
  { token: 'customer.company', label: 'Customer Company', group: 'Customer' },
  { token: 'customer.city', label: 'Customer City', group: 'Customer' },
];

const SCALAR_COMPANY: Chip[] = [
  { token: 'company.name', label: 'Company Name', group: 'Company' },
  { token: 'company.gstin', label: 'Company GSTIN', group: 'Company' },
];

const SCALAR_TOTALS: Chip[] = [
  { token: 'totals.subtotal', label: 'Subtotal', group: 'Totals' },
  { token: 'totals.tax', label: 'Tax', group: 'Totals' },
  { token: 'totals.total', label: 'Grand Total', group: 'Totals' },
];

const BLOCK_TOKENS: Chip[] = [
  { token: 'lineItems', label: '📊 Line Items', group: 'Blocks', fragment: true },
  { token: 'roiGrid', label: '📊 ROI Grid', group: 'Blocks', fragment: true },
  { token: 'engineeringNotes', label: '📊 Engineering Notes', group: 'Blocks', fragment: true },
];

/**
 * buildChipPalette — derive the full chip palette from a live CalcSpec (+ its
 * declared inputs). Kept name-parallel to the backend so the two stay in sync.
 */
export function buildChipPalette(spec: CalcSpec, inputs: StudioInputField[] = []): Chip[] {
  const chips: Chip[] = [];

  // Inputs (from the designer) → {{input.<key>}}
  const inputList = inputs.length > 0 ? inputs : spec.inputs ?? [];
  for (const f of inputList) {
    chips.push({ token: `input.${f.key}`, label: f.label || f.key, group: 'Inputs' });
  }

  // Outputs (formula outputKeys) → {{output.<key>}}
  for (const f of spec.formulas ?? []) {
    chips.push({ token: `output.${f.outputKey}`, label: f.outputKey, group: 'Outputs' });
  }

  // Recommendation slots → {{rec.<slot>.sku}} / .score / .price
  const slots = spec.recommendation?.slots ?? [];
  const slotNames = slots.length > 0 ? Array.from(new Set(slots.map((s) => s.slot))) : (['primary'] as const);
  for (const slot of slotNames) {
    chips.push({ token: `rec.${slot}.sku`, label: `${slot} SKU`, group: 'Recommendation' });
    chips.push({ token: `rec.${slot}.price`, label: `${slot} Price`, group: 'Recommendation' });
  }

  chips.push(...BLOCK_TOKENS, ...SCALAR_CUSTOMER, ...SCALAR_COMPANY, ...SCALAR_TOTALS);
  return chips;
}

export const FRAGMENT_TOKENS = new Set(BLOCK_TOKENS.map((c) => c.token));

export function chipLabel(token: string, palette: Chip[]): string {
  const hit = palette.find((c) => c.token === token);
  return hit ? hit.label : token.replace(/[._]/g, ' ');
}
